import { environment } from '@/config/environment'
import { fetchAccessToken } from '@/api/AuthConfig'
import { Sensor } from '@/features/dashboard/models'
import { AxiosError } from 'axios'
import {
    Api,
    DeviceDto,
    PlantDto,
    ProjectDto,
    UserSettingsDto,
    OmniaTimeSeriesDto,
    SystemDto,
    StidTagsResponseModel,
    AreaDto,
    SuperiorTagDto,
    OnboardSensorsResponseDto,
    InstalledSensorsDataDto,
    TimeseriesDto,
    UpdateSensorsDataDto,
    EditSensorResponseDto,
    TrendDto,
    TagDataDto,
    FileHolderDto,
    OnboardPayload,
    TimeseriesLatestDto,
    SensorMetadataConfigurationResponseDto,
    DownlinkType,
    VendorSerialNumberCheckResultDto,
    ExistingImageDto,
    InstalledSensorStatus,
    BatteryStatus,
    SignalQuality,
} from '@/core/models/types'

export const MIN_TAG_SEARCH_LENGTH = 3

async function configureApi(): Promise<Api<null>> {
    const token = await fetchAccessToken(environment.BACKEND_API_SCOPE)
    return new Api<null>({
        secure: true,
        baseURL: environment.BACKEND_URL,
        securityWorker: () => ({ headers: { Authorization: `Bearer ${token}` } }),
    })
}

export async function getOnboardedSensors(plant?: string): Promise<InstalledSensorsDataDto[]> {
    const api = await configureApi()
    const response = plant
        ? await api.api.installedSensorsPlantSensorsDetail(plant)
        : await api.api.installedSensorsAllList()
    return response.data
}

export interface OnboardedSensorsPageParams {
    page: number
    pageSize: number
    search?: string
    status?: string[]
    plant?: string
    project?: string
    area?: string
    device?: string
    protocol?: string
    sensorType?: string
    superiorTag?: string
    system?: string
    batteryStatus?: string
    signalQuality?: string
    sortBy?: string
    sortDir?: 'asc' | 'desc'
}

export interface OnboardedSensorsPageResult {
    items: InstalledSensorsDataDto[]
    totalCount: number | undefined
}

export async function getOnboardedSensorsPage(params: OnboardedSensorsPageParams): Promise<OnboardedSensorsPageResult> {
    const api = await configureApi()
    const query = {
        Page: params.page,
        PageSize: params.pageSize,
        Search: params.search,
        Status: params.status?.length ? (params.status as InstalledSensorStatus[]) : undefined,
        Project: params.project,
        Area: params.area,
        Device: params.device,
        Protocol: params.protocol,
        SensorType: params.sensorType,
        SuperiorTag: params.superiorTag,
        System: params.system,
        BatteryStatus: params.batteryStatus as BatteryStatus | undefined,
        SignalQuality: params.signalQuality as SignalQuality | undefined,
        SortBy: params.sortBy,
        SortDir: params.sortDir,
    }
    const response = params.plant
        ? await api.api.installedSensorsPlantSensorsDetail(params.plant, query)
        : await api.api.installedSensorsAllList(query)
    const totalHeader = response.headers['x-total-count']
    const parsed = totalHeader != null ? Number(totalHeader) : NaN
    const totalCount = Number.isFinite(parsed) ? parsed : undefined
    return { items: response.data, totalCount }
}

export async function getOnboardedSensorsTagsList(tag: string[]): Promise<InstalledSensorsDataDto[]> {
    const api = await configureApi()
    const response = await api.api.installedSensorsTagsList({ tags: tag })
    return response.data
}

export async function onboardedSensors(
    plant: string,
    onboardingRequest: OnboardPayload
): Promise<OnboardSensorsResponseDto> {
    const api = await configureApi()

    if (!onboardingRequest.sensor) {
        throw new Error('Sensor is required')
    }

    const formData = buildOnboardingFormData({
        plant: requireString(onboardingRequest.plant, 'Plant'),
        project: requireString(onboardingRequest.project, 'Project'),
        sensorModel: requireString(onboardingRequest.sensorModel, 'SensorModel'),
        plantId: onboardingRequest.plantId ?? undefined,
        sensor: onboardingRequest.sensor,
    })

    const { data } = await api.onboarding.sensorPlantAsyncCreate(plant, formData as any, { commitStatusChange: true }, {
        // IMPORTANT: prevent the client from trying to rebuild FormData
        type: undefined as any,
    } as any)
    return data
}

export async function saveSensorsToDraft(
    plant: string,
    onboardingRequest: OnboardPayload
): Promise<OnboardSensorsResponseDto> {
    const api = await configureApi()

    if (!onboardingRequest.sensor) {
        throw new Error('Sensor is required')
    }

    const formData = buildOnboardingFormData({
        plant: requireString(onboardingRequest.plant, 'Plant'),
        project: requireString(onboardingRequest.project, 'Project'),
        sensorModel: requireString(onboardingRequest.sensorModel, 'SensorModel'),
        plantId: onboardingRequest.plantId ?? undefined,
        sensor: onboardingRequest.sensor,
    })

    const { data } = await api.onboarding.sensorPlantDraftCreate(
        plant,
        formData as any,
        {
            // IMPORTANT: prevent the client from trying to rebuild FormData
            type: undefined as any,
        } as any
    )
    return data
}

export async function getPlants(): Promise<PlantDto[]> {
    const api = await configureApi()
    const response = await api.plants.getPlants({ onboardedOnly: true })
    return response.data
}

export async function getPlantStatusAccess(plant: string): Promise<boolean> {
    const api = await configureApi()
    const response = await api.plants.getPlantStatusAccess({ plant })
    return response.data
}

export async function getProjects(plant: string): Promise<ProjectDto[]> {
    if (plant === '') return []
    const api = await configureApi()
    const response = await api.plants.projectsPlantDetail(plant)
    return response.data
}

export async function getSystems(plant: string): Promise<SystemDto[]> {
    if (plant === '') return []
    const api = await configureApi()
    const response = await api.plants.systemsPlantDetail(plant)
    return response.data
}

export async function getAreas(plant: string): Promise<AreaDto[]> {
    if (plant === '') return []
    const api = await configureApi()
    const response = await api.plants.areasPlantDetail(plant)
    return response.data
}

export async function getInstalledSensorsByVendorAndModel(
    vendorSerialNumber: string,
    sensorModel: string,
    excludeSensorDbKey: string
): Promise<VendorSerialNumberCheckResultDto> {
    if (vendorSerialNumber === '') return { alreadyInstalledSensors: [], notReadyForOnboarding: false }
    if (sensorModel === '') return { alreadyInstalledSensors: [], notReadyForOnboarding: false }
    const api = await configureApi()
    const response = await api.api.installedSensorsSensorsDetail(vendorSerialNumber, sensorModel, excludeSensorDbKey)
    return response.data
}

// Returns null when the serial number is not pre-provisioned, so onboarding falls back to manual device selection.
export async function getDeviceByVendorSerialNumber(vendorSerialNumber: string): Promise<DeviceDto | null> {
    if (vendorSerialNumber === '') return null
    const api = await configureApi()
    try {
        const response = await api.api.installedSensorsDeviceDetail(encodeURIComponent(vendorSerialNumber))
        return response.data
    } catch (e) {
        if ((e as AxiosError)?.response?.status === 404) return null
        throw e
    }
}

export async function getTimeSeriesValues(timeSeriesTableId: string, from?: string): Promise<TrendDto[]> {
    if (timeSeriesTableId === '') return []
    const api = await configureApi()
    const response = await api.omniaTimeseries.timeseriesDetail(timeSeriesTableId, from ? { from } : undefined)
    return response.data
}

export async function getLatestTimeSeriesValues(timeSeriesTableId: string): Promise<TimeseriesLatestDto> {
    const api = await configureApi()
    const response = await api.api.installedSensorsTimeseriesLatestList({ timeSeriesTableDbKey: timeSeriesTableId })
    return response.data
}

export async function getSuperiorTags(plant: string): Promise<SuperiorTagDto[]> {
    if (plant === '') return []
    const api = await configureApi()
    const response = await api.plants.superiortagsPlantDetail(plant)
    return response.data
}

export async function getFilteredTags(
    plant: string,
    project: string,
    device: string,
    system: string,
    area: string
): Promise<StidTagsResponseModel[]> {
    if (plant === '') return []
    if (project === '' && system === '' && area === '') {
        return []
    }
    const api = await configureApi()
    const response = await api.stid.plantTagsDetail(plant, {
        project: project,
        device: device,
        system: system,
        area: area,
    })
    return response.data
}

// Type-ahead search for STID tags within a plant. Requires a few characters so we don't pull the whole plant.
export async function searchStidTags(plant: string, tagNo: string): Promise<StidTagsResponseModel[]> {
    const search = tagNo.trim()
    if (plant === '' || search.length < MIN_TAG_SEARCH_LENGTH) return []
    const api = await configureApi()
    const response = await api.stid.plantTagsDetail(plant, { tagNo: search })
    return response.data
}

export async function getMeasurements(plant: string, tag: string): Promise<OmniaTimeSeriesDto[]> {
    if (plant === '' || tag === '') return []
    const api = await configureApi()
    const response = await api.api.measurementsPlantTagDetail(plant, tag)
    return response.data
}

export async function getUserSettings(): Promise<UserSettingsDto> {
    const api = await configureApi()
    const response = await api.api.getUserSettings()
    return response.data
}

export async function updateUserSettings(userSettings: UserSettingsDto): Promise<void> {
    const api = await configureApi()
    await api.api.upsertUserSettings(userSettings)
}

export async function updateSensor(
    id: string,
    plant: string,
    sensor: UpdateSensorsDataDto
): Promise<EditSensorResponseDto> {
    const api = await configureApi()
    const response = await api.onboarding.sensorPlantEditUpdate(id, plant, sensor)
    return response.data
}

export async function updateImageDescription(plant: string, docNo: string, description: string): Promise<boolean> {
    const api = await configureApi()
    const response = await api.stid.updateImageDescription(plant, docNo, description)
    return response.data
}

export async function insertImage(plant: string, tagNo: string, file: File, description: string): Promise<boolean> {
    const api = await configureApi()

    const response = await api.stid.insertImage(plant, tagNo, {
        File: file,
        Description: description,
    })
    return response.data
}

export async function deleteImage(plant: string, docNo: string): Promise<boolean> {
    const api = await configureApi()
    const response = await api.stid.deleteImage(plant, docNo)
    return response.data
}

export async function getDevices(): Promise<DeviceDto[]> {
    const api = await configureApi()
    const response = await api.all.getAll()
    return response.data
}

export async function getProtocols(): Promise<string[]> {
    const api = await configureApi()
    const response = await api.api.sensorMetadataProtocolsList()
    return response.data
}

export async function getTagDetails(plant: string, tags: string[]): Promise<TagDataDto[]> {
    const api = await configureApi()
    const response = await api.stid.getTagData(plant, tags)
    return response.data
}

export async function getSensorDownlinkCapability(sensorDbKey: string): Promise<{ supportedTypes: string[] }> {
    const api = await configureApi()
    const response = await api.api.downlinkSensorCapabilityDetail(sensorDbKey)
    // Cast needed until the backend is restarted and the api client is regenerated with the typed response.
    return response.data as unknown as { supportedTypes: string[] }
}

export async function getSensorMetadataConfigurations(
    sensorDbKey: string
): Promise<SensorMetadataConfigurationResponseDto> {
    const api = await configureApi()
    const response = await api.api.installedSensorsSensorConfigurationsDetail(sensorDbKey)
    return response.data
}

export async function updateSensorFrequency(
    plant: string,
    sensorDbKey: string,
    seconds: number,
    type: number
): Promise<void> {
    const api = await configureApi()
    await api.api.downlinkPlantSensorFrequencyCreate(plant, sensorDbKey, { seconds, type: type as DownlinkType })
}

export async function getImage(plant: string, fileId: number): Promise<FileHolderDto> {
    const api = await configureApi()
    const response = await api.stid.getImage(plant, fileId)
    return response.data
}

export async function getSensorTimeSeriesValues(sensorDbKey: string): Promise<TimeseriesDto> {
    const api = await configureApi()
    const response = await api.api.installedSensorsTimeseriesList({ sensorDbKey: sensorDbKey })
    return response.data
}

export async function getSensorImages(sensorDbKey: string): Promise<ExistingImageDto[]> {
    if (!sensorDbKey) return []
    const api = await configureApi()
    const response = await api.api.installedSensorsImagesList({ sensorDbKey })
    return response.data ?? []
}

export async function deleteSensor(sensor: Sensor, userName: string): Promise<void> {
    if (!sensor.plant || !sensor.sensorDbKey) return

    const api = await configureApi()

    await api.api.installedSensorsPlantSensorDeleteDelete(sensor.plant, sensor.sensorDbKey, { userName })
}

function buildOnboardingFormData(req: {
    plant: string
    project: string
    sensorModel: string
    plantId?: string | null
    sensor: Partial<Sensor>
}) {
    const fd = new FormData()

    fd.append('Plant', req.plant)
    fd.append('Project', req.project)
    fd.append('SensorModel', req.sensorModel)

    if (req.plantId != null) {
        fd.append('PlantId', req.plantId)
    }

    const p = 'Sensor'
    const s = req.sensor

    if (s.sensorDbKey != null) fd.append(`${p}.SensorDbKey`, String(s.sensorDbKey))

    if (s.existingImages?.length) {
        s.existingImages.forEach((img, i) => {
            if (img.url != null) {
                fd.append(`${p}.ExistingImageUrls[${i}].Url`, img.url)
            }

            if (img.description != null) {
                fd.append(`${p}.ExistingImageUrls[${i}].Description`, img.description)
            }
        })
    }

    if (s.superiorTag != null) fd.append(`${p}.SuperiorTag`, s.superiorTag)
    if (s.area != null) fd.append(`${p}.Area`, s.area)
    if (s.coordinates != null) fd.append(`${p}.Coordinates`, s.coordinates)
    if (s.tag != null) fd.append(`${p}.Tag`, s.tag)
    if (s.vendorSerialNumber != null) fd.append(`${p}.VendorSerialNumber`, s.vendorSerialNumber)
    if (s.userAddedDescription != null) fd.append(`${p}.UserAddedDescription`, s.userAddedDescription)

    fd.append(`${p}.CoordinatesNotAvailable`, String(!!s.coordinatesNotAvailable))

    const files = s.imageFiles ?? []
    const descs = s.imageDescriptions ?? []
    const count = Math.max(files.length, descs.length)

    for (let j = 0; j < count; j++) {
        const ip = `${p}.Images[${j}]`
        const file = files[j]
        const description = descs[j]

        if (description != null && description !== '') {
            fd.append(`${ip}.Description`, description)
        }

        if (file) {
            fd.append(`${ip}.File`, file, file.name)
        }
    }

    return fd
}

function requireString(value: string | null | undefined, name: string): string {
    if (value == null || value.trim() === '') {
        throw new Error(`${name} is required`)
    }
    return value
}
